
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, Lock, User, Building, Loader, UserPlus } from 'lucide-react';
import { InvitationDetails } from '@/hooks/useInvitationHandler';
import { Link } from 'react-router-dom';

interface InvitationSignUpFormProps {
  invitation: InvitationDetails;
  onSubmit: (email: string, password: string, firstName: string, lastName: string) => Promise<{ error: any }>;
  isLoading: boolean;
}

export const InvitationSignUpForm = ({ invitation, onSubmit, isLoading }: InvitationSignUpFormProps) => {
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState(invitation.firstName || '');
  const [lastName, setLastName] = useState(invitation.lastName || '');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      return;
    }

    await onSubmit(invitation.email, password, firstName, lastName);
  };

  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <div className="space-y-4">
      {/* Invitation Context */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <UserPlus className="h-4 w-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Invitation Details</span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div>
            <span className="text-gray-600">Email:</span> 
            <span className="ml-2 font-medium">{invitation.email}</span>
          </div>
          
          {invitation.organizationName && (
            <div className="bg-primary/5 p-3 rounded-lg border border-primary/20 mt-2">
              <div className="flex items-center gap-2">
                <Building className="h-4 w-4 text-primary" />
                <div className="flex-1">
                  <span className="text-xs text-gray-600 block">Joining Organization</span>
                  <span className="font-semibold text-primary">{invitation.organizationName}</span>
                </div>
                <Lock className="h-3 w-3 text-gray-400" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Your account will be automatically added to this organization. This cannot be changed.
              </p>
            </div>
          )}
          
          <div className="flex items-center gap-2 mt-2">
            <span className="text-gray-600">Roles:</span>
            <div className="flex gap-1">
              {invitation.roles.map((role) => (
                <Badge key={role} variant="secondary" className="text-xs">
                  {formatRole(role)}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                id="firstName"
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="pl-10"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="invitationEmail">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="invitationEmail"
              type="email"
              value={invitation.email}
              className="pl-10 bg-gray-50"
              disabled
            />
          </div>
          <p className="text-xs text-gray-500">
            This email is pre-filled from your invitation and cannot be changed.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="invitationPassword">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="invitationPassword"
              type="password"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmInvitationPassword">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              id="confirmInvitationPassword"
              type="password"
              placeholder="Confirm your password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="pl-10"
              required
            />
          </div>
          {password && confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-600">Passwords do not match</p>
          )}
        </div>

        <div className="flex items-start gap-2">
          <Checkbox
            id="acceptTerms"
            checked={acceptedTerms}
            onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
            className="mt-1"
          />
          <Label htmlFor="acceptTerms" className="text-sm leading-relaxed cursor-pointer">
            I agree to the{' '}
            <Link to="/terms" target="_blank" className="text-copper hover:underline font-semibold">
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link to="/privacy" target="_blank" className="text-copper hover:underline font-semibold">
              Privacy Policy
            </Link>
          </Label>
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={isLoading || password !== confirmPassword || !password || !firstName || !lastName || !acceptedTerms}
        >
          {isLoading ? (
            <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Creating account...
            </>
          ) : (
            'Accept Invitation & Create Account'
          )}
        </Button>
      </form>
    </div>
  );
};
